import {CARD_TEXT, CardType, GameState, TurnState, UserAction} from "../../../types/game";
import styles from "../GameScreen.module.scss";
import {noAction} from "../../../utils";
import {GameDeckProps} from "../../../types/props";
import {Flipped} from "react-flip-toolkit";

const DeckSlot = ({game, size, playerId, onDeckCardClick, onReturnToLobby}: GameDeckProps) => {
    const {
        currentPlayerId,
        turnState,
        lastPlayerAction,
        topDeckCard,
        lastDiscardCard,
        sameCardTossed,
        discardCardsLength,
        deckCardsLength,
        lastTossCartEvent
    } = game.gameData;

    const isResultsReveal = game.state === GameState.RESULTS_REVEAL;
    const isCardTakePhase = turnState === TurnState.CARD_TAKE;
    const isClickable = isCardTakePhase && playerId === currentPlayerId;
    const sizeClass = size === "sm" ? styles.cardSlot__small : styles.cardSlot__medium;

    const renderEmptyPile = () => (
        <div className={styles.gameScreen__pileEmpty}>Empty</div>
    );

    const constructDeckCardClass: () => string = () => {
        const isDeckCardAvailable = isCardTakePhase
            && topDeckCard
            && topDeckCard.type !== CardType.CARD_BACK
            && topDeckCard.type !== CardType.EMPTY;

        const cardClass = isDeckCardAvailable ? styles["cardSlot--front"] : styles["cardSlot--back"];
        const clickableClass = isClickable ? "" : styles["cardSlot__disabled"];
        const isEventMatching = lastPlayerAction?.userAction === UserAction.CHOOSE_CARD
            || lastPlayerAction?.userAction === UserAction.FORCE_CARD_TAKE;
        const sourceClass = (isEventMatching && lastPlayerAction?.cardSlot === -1) ? styles["cardSlot--moveSource"] : "";

        return `${styles.cardSlot} ${cardClass} ${sizeClass} ${clickableClass} ${sourceClass}`;
    };

    function constructDiscardCardClass() {
        const isEmpty = lastDiscardCard.type === CardType.EMPTY;
        const isFaceDown = lastDiscardCard.type === CardType.CARD_BACK;

        const cardClass = isEmpty
            ? styles["cardSlot--empty"]
            : isFaceDown
                ? styles["cardSlot--back"]
                : styles["cardSlot--front"];

        const targetClass = lastPlayerAction?.userAction === UserAction.CHOOSE_CARD ? styles["cardSlot--moveTarget"] : "";
        return `${styles["cardSlot"]} ${cardClass} ${styles["cardSlot__disabled"]} ${sizeClass} ${targetClass}`;
    }

    function constructTossedDiscardCardClass() {
        const targetClass = (lastTossCartEvent?.success && (lastTossCartEvent?.version ?? 0) >= (lastPlayerAction?.version ?? 0))
            ? styles["cardSlot__sameCardOverlay--tossed"] : "";
        return `${styles.cardSlot} ${styles["cardSlot--front"]} ${styles["cardSlot__disabled"]} ${sizeClass} ${styles.cardSlot__sameCardOverlay} ${targetClass}`;
    }

    return (
        <div
            className={styles.gameScreen__centerWrapper}
            onContextMenu={e => e.preventDefault()}
        >
            <div className={styles.gameScreen__center}>
                <div className={styles.gameScreen__centerStack}>
                    {deckCardsLength > 0 ? (
                        <>
                            <Flipped key={topDeckCard.id} flipId={`card-${topDeckCard.id}`}>
                                <div
                                    className={constructDeckCardClass()}
                                    onClick={isClickable ? () => onDeckCardClick(playerId, -1) : noAction}
                                >
                                    <span className={styles.cardSlot__label}>{CARD_TEXT[topDeckCard.type]}</span>
                                </div>
                            </Flipped>
                            <span className={styles.gameScreen__stackBadge}>
                                {deckCardsLength}
                            </span>
                        </>
                    ) : renderEmptyPile()}
                </div>

                <div className={styles.gameScreen__centerStack}>
                    {lastDiscardCard.type === CardType.EMPTY ? renderEmptyPile() : (
                        <>
                            <Flipped key={lastDiscardCard.id} flipId={`card-${lastDiscardCard.id}`}>
                                <div className={constructDiscardCardClass()}>
                                    <span
                                        className={styles.cardSlot__label}>{CARD_TEXT[lastDiscardCard.type]}
                                    </span>
                                </div>
                            </Flipped>
                            {sameCardTossed && (
                                <Flipped key={sameCardTossed.id} flipId={`card-${sameCardTossed.id}`}>
                                    <div className={constructTossedDiscardCardClass()}>
                                        <span
                                            className={styles.cardSlot__label}>{CARD_TEXT[sameCardTossed.type]}
                                        </span>
                                    </div>
                                </Flipped>
                            )}
                            <span className={styles.gameScreen__stackBadge}>
                                {discardCardsLength}
                            </span>
                        </>
                    )}
                </div>
                {isResultsReveal && (
                    <div className={styles.gameScreen__centerOverlay}>
                        <button
                            type="button"
                            className={styles.gameScreen__returnButton}
                            onClick={onReturnToLobby ?? noAction}
                        >
                            Return to lobby
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default DeckSlot;